import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import * as rpc from "../../../common/jsonRpcRoutes";
import {
  isAbortedRequest,
  isTimedOutRequest,
  useRpcCaller,
  useWidgetRequests,
  type RpcCaller,
} from "../context/WidgetRequestContext";
import type {
  CustomerPreOrder,
  CustomerPreOrderItemUpdateRequest,
  PreOrderItem,
  TaxBreakdownEntry,
  Booking,
  PreorderMenu,
} from "../../../common/jsonRpcTypes";
import analytics, { describeCartChange } from "../../analytics";
import { getActualItemCountForMenu, preorderItemKey, type MenuLimits } from "../preOrders/preOrderUtils";
import { getHidePricesByMenu } from "../../utils/menuPriceHelpers";
import { isPaidByGroupMemberForBasket } from "../../utils/preorderSettingsUtils";
import { validateMinimumSpend } from "../../utils/menuValidation";

interface PreservedPreOrderItems {
  items: PreOrderItem[];
  totalCost: number;
  shouldHidePrices: boolean;
  hidePricesByMenu: Record<string, boolean>;
  serviceChargeAmount?: number;
  serviceChargePercentage?: number;
  totalCostWithService?: number;
  taxInclusive?: boolean;
  taxBreakdown?: TaxBreakdownEntry[];
  totalNet?: number;
  totalGross?: number;
  taxLabel?: string;
}

interface UsePreorderStateParams {
  bookingId: string;
  orderId?: string;
  bookingData: Booking | null;
  forNames: string[];
  onLocalPreOrderLoaded?: (localPreOrder: any) => void;
  /** When provided, skips the /getCustomerPreOrder API call and uses this data directly. */
  initialCustomerPreOrder?: CustomerPreOrder;
  /** Preorder menus for minimum spend validation */
  preorderMenus?: PreorderMenu[];
  /** Deposit information for validation logic */
  depositRequired?: import("../../../common/jsonRpcTypes").DepositRequired;
}

interface UsePreorderStateReturn {
  customerPreOrder: CustomerPreOrder | undefined;
  customerPreOrders: Record<string, CustomerPreOrder>;
  setCustomerPreOrder: React.Dispatch<React.SetStateAction<CustomerPreOrder | undefined>>;
  setCustomerPreOrders: React.Dispatch<React.SetStateAction<Record<string, CustomerPreOrder>>>;
  preservedPreOrderItems: PreservedPreOrderItems | undefined;
  isLoadingPreorderData: boolean;
  isPreorderComplete: boolean;
  isPreorderUnavailable: boolean;
  preorderUnavailableMessage: string | null;
  preorderLimitMessage: string | null;
  menuLimits: MenuLimits;
  setMenuLimits: React.Dispatch<React.SetStateAction<MenuLimits>>;
  externalValidationErrors: { [menuId: string]: string };
  originalServiceChargePercentage: number | null;
  confirmedEmail: string;
  confirmedAmount: number;
  showSuccess: boolean;
  updateCustomerPreOrder: (request: CustomerPreOrderItemUpdateRequest) => Promise<boolean>;
  /** Whether an update for this item is in flight, so the menu and basket can lock its row together. */
  isItemPending: (itemId: string, menuId: string, personIndex: number) => boolean;
  refreshCustomerPreOrder: (preserveExistingItems?: boolean) => Promise<void>;
  /** Re-merges the basket from `/getAllBookingTypeBaskets` for the current booking. */
  reloadServerBasket: () => Promise<void>;
  handleServiceChargeChange: (percentage: number) => Promise<boolean>;
  handleValidationChange: (isValid: boolean) => void;
  handleMaxItemsError: (menuId: string, errorMessage: string) => void;
  handlePaymentSuccess: (email?: string, amount?: number) => void;
  savePreorders: (details: { name: string; email: string; customerNotes?: string }) => Promise<void>;
  isOrderValid: boolean;
  hidePricesByMenu: Record<string, boolean>;
  shouldHidePrices: boolean;
  paidByGroupMember: boolean;
}

/**
 * Finds an item's catalogue entry, preferring the venue catalogue and falling
 * back to the loaded menus, which are the only source in the booking flow.
 */
function findCatalogueItem(
  preOrder: CustomerPreOrder,
  itemId: string,
): { name?: string; price?: number | string } | undefined {
  const venueItem = preOrder.booking.venue.preorder_items.find((item) => item.id === itemId);
  if (venueItem) return venueItem;

  for (const menu of preOrder.menus ?? []) {
    const menuItem = menu.items.find((item) => item.type !== "heading" && item.id === itemId);
    if (menuItem && menuItem.type !== "heading") return menuItem;
  }

  return undefined;
}

/**
 * Reports a pre-order quantity change to analytics as `add_to_cart` or
 * `remove_from_cart`, using the item's catalogue entry for its name and price
 * when the basket does not yet contain the line.
 */
function describeItemUpdate(
  preOrder: CustomerPreOrder,
  request: CustomerPreOrderItemUpdateRequest,
): (() => void) | null {
  // Name assignments and reassignments carry no quantity change: they take a
  // separate server path that leaves the basket as it is.
  if (!request.itemId || request.reassignExisting) return null;

  const catalogueItem = findCatalogueItem(preOrder, request.itemId);
  const cataloguePrice = typeof catalogueItem?.price === "string" ? Number(catalogueItem.price) : catalogueItem?.price;

  const change = describeCartChange(preOrder.items ?? [], {
    itemId: request.itemId,
    amount: request.amount,
    name: catalogueItem?.name,
    price: Number.isFinite(cataloguePrice) ? cataloguePrice : undefined,
  });

  if (!change) return null;

  const currency = preOrder.booking.venue.currency;

  return () => {
    if (change.event === "add_to_cart") {
      void analytics.addToCart(change.item, currency);
    } else {
      void analytics.removeFromCart(change.item, currency);
    }
  };
}

function loadPreorderFromLocalStorage(call: RpcCaller, bookingId: string) {
  return call("/loadClientBasket", {
    booking_id: bookingId,
  }).then((basket) => {
    if (!basket) {
      return undefined;
    }
    return basket;
  });
}

// The server keys baskets by booking type ("default" when none was sent); a single basket is
// always the current one, whatever its key.
function selectServerBasket(
  baskets: Record<string, CustomerPreOrder>,
  bookingType: string | undefined,
): CustomerPreOrder | undefined {
  const keyed = (bookingType ? baskets[bookingType] : undefined) ?? baskets.default;
  if (keyed) return keyed;
  const all = Object.values(baskets);
  return all.length === 1 ? all[0] : undefined;
}

export function usePreorderState({
  bookingId,
  orderId,
  bookingData,
  forNames,
  onLocalPreOrderLoaded,
  initialCustomerPreOrder,
  preorderMenus = [],
  depositRequired,
}: UsePreorderStateParams): UsePreorderStateReturn {
  const call = useRpcCaller();
  const consumeInterruption = useWidgetRequests()?.consumeInterruption;
  const { t } = useTranslation();
  const [customerPreOrder, setCustomerPreOrder] = useState<CustomerPreOrder | undefined>(undefined);
  const [customerPreOrders, setCustomerPreOrders] = useState<Record<string, CustomerPreOrder>>({});
  const [preservedPreOrderItems, setPreservedPreOrderItems] = useState<PreservedPreOrderItems | undefined>(undefined);
  const [isLoadingPreorderData, setIsLoadingPreorderData] = useState(true);
  const [isPreorderComplete, setIsPreorderComplete] = useState(false);
  const [isPreorderUnavailable, setIsPreorderUnavailable] = useState(false);
  const [preorderUnavailableMessage, setPreorderUnavailableMessage] = useState<string | null>(null);
  const [preorderLimitMessage, setPreorderLimitMessage] = useState<string | null>(null);
  const [menuLimits, setMenuLimits] = useState<MenuLimits>({});
  const [externalValidationErrors, setExternalValidationErrors] = useState<{ [menuId: string]: string }>({});
  const [originalServiceChargePercentage, setOriginalServiceChargePercentage] = useState<number | null>(null);
  const [confirmedEmail, setConfirmedEmail] = useState<string>("");
  const [confirmedAmount, setConfirmedAmount] = useState<number>(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isOrderValid, setIsOrderValid] = useState(true);
  const [pendingItemCounts, setPendingItemCounts] = useState<ReadonlyMap<string, number>>(new Map());
  const [editingOrderId] = useState<string | undefined>(orderId);
  const hasAppliedInitialPreOrder = useRef(false);

  // Function to fetch all booking type baskets
  const fetchAllBookingTypeBaskets = useCallback(
    async (currentBookingId: string) => {
      try {
        const baskets = await call("/getAllBookingTypeBaskets", { booking_id: currentBookingId });
        const preOrdersMap: Record<string, CustomerPreOrder> = {};

        if (Array.isArray(baskets)) {
          for (const basket of baskets) {
            if (basket.bookingType && basket.items) {
              preOrdersMap[basket.bookingType] = {
                items: basket.items,
                menus: basket.menus ?? [],
                booking: basket.booking ?? {},
                forNames: basket.forNames ?? [],
                serviceChargePercentage: basket.serviceChargePercentage,
                serviceChargeAmount: basket.serviceChargeAmount,
                totalCost: basket.totalCost,
                totalCostWithService: basket.totalCostWithService,
                requiredPaymentAmount: basket.requiredPaymentAmount,
                tax_inclusive: basket.tax_inclusive,
                tax_label: basket.tax_label,
                tax_breakdown: basket.tax_breakdown,
                total_net: basket.total_net,
                total_gross: basket.total_gross,
                bookingTypeName: basket.bookingTypeName,
              } as CustomerPreOrder;
            }
          }
        }

        setCustomerPreOrders(preOrdersMap);
        return preOrdersMap;
      } catch (error) {
        if (isAbortedRequest(error)) return {};
        console.error("Failed to fetch all booking type baskets:", error);
        return {};
      }
    },
    [call],
  );

  // Derive price visibility and payment settings from customerPreOrder
  const customerPreorderSettings = customerPreOrder?.customer_preorder_settings ?? {};
  const hidePricesByMenu: Record<string, boolean> = useMemo(
    () => getHidePricesByMenu(customerPreorderSettings),
    [customerPreorderSettings],
  );
  const paidByGroupMember: boolean = useMemo(() => isPaidByGroupMemberForBasket(customerPreOrder), [customerPreOrder]);
  const hidePrices: boolean = useMemo(() => Object.values(hidePricesByMenu).some(Boolean), [hidePricesByMenu]);
  // When initialCustomerPreOrder is provided (the /book flow), prices should always be visible
  // because the lead booker is placing the order directly — there is no group-member payment concept.
  // Scoped to the /book flow only to preserve existing /place-your-order behaviour.
  const shouldHidePrices = initialCustomerPreOrder ? false : hidePrices || !paidByGroupMember;

  // Check pre-order availability on page load (only for new orders, not edit orders)
  // Skipped when initialCustomerPreOrder is provided (the /book flow already knows preordering is available).
  useEffect(() => {
    if (initialCustomerPreOrder) return;

    const checkPreorderAvailability = async () => {
      // Skip check if this is an edit preorder page (has order_id)
      if (orderId) {
        return;
      }

      try {
        const result = await call("/checkPreorderAvailability", {
          booking_id: bookingId,
        });

        if (!result.available) {
          // Prefer Collins' specific reason (e.g. "closed" vs "limit reached");
          // fall back to a general translated message when none is provided.
          setPreorderLimitMessage(
            result.message ?? t("preorders_unavailable", "Pre-orders for this booking are currently unavailable."),
          );
          setIsLoadingPreorderData(false);
        }
      } catch (error) {
        if (isAbortedRequest(error)) return;
        console.error("Error checking pre-order availability:", error);
      }
    };

    if (bookingId) {
      checkPreorderAvailability();
    }
  }, [call, bookingId, orderId, initialCustomerPreOrder, t]);

  const applyCostValuesFromBasket = useCallback((preOrder: CustomerPreOrder, basket: any): CustomerPreOrder => {
    if (basket.requiredPaymentAmount !== undefined) preOrder.requiredPaymentAmount = basket.requiredPaymentAmount;
    if (basket.serviceChargeAmount !== undefined) preOrder.serviceChargeAmount = basket.serviceChargeAmount;
    if (basket.serviceChargePercentage !== undefined) preOrder.serviceChargePercentage = basket.serviceChargePercentage;
    if (basket.totalCost !== undefined) preOrder.totalCost = basket.totalCost;
    if (basket.totalCostWithService !== undefined) preOrder.totalCostWithService = basket.totalCostWithService;
    return preOrder;
  }, []);

  const restoreCachedCostValues = useCallback(
    (preOrder: CustomerPreOrder, localPreOrder: Partial<CustomerPreOrder>) => {
      if (!localPreOrder?.items || localPreOrder.items.length === 0) return preOrder;

      const costFields = [
        "totalCost",
        "totalCostWithService",
        "serviceChargeAmount",
        "serviceChargePercentage",
        "requiredPaymentAmount",
      ] as const;

      const updated = { ...preOrder };
      for (const field of costFields) {
        if (localPreOrder[field] !== undefined) {
          updated[field] = localPreOrder[field];
        }
      }

      return updated;
    },
    [],
  );

  const mergeLocalAndRemotePreorder = useCallback(
    async (mergeBookingId: string, orderIdToEdit?: string) => {
      // When editing an existing order, load the basket into Redis first
      if (orderIdToEdit) {
        const basket = await call("/loadClientBasket", {
          booking_id: mergeBookingId,
          order_id: orderIdToEdit,
        });

        const preOrder = await call("/getCustomerPreOrder", {
          booking_id: mergeBookingId,
          order_id: orderIdToEdit,
        });

        if (!preOrder) return null;

        return { customerPreOrder: applyCostValuesFromBasket(preOrder, basket), localPreOrder: null };
      }

      // Normal flow: merge local storage with remote data
      const localPreOrder = await loadPreorderFromLocalStorage(call, mergeBookingId);
      const preOrder = await call("/getCustomerPreOrder", { booking_id: mergeBookingId });

      if (!preOrder) return null;

      preOrder.items = localPreOrder?.items ?? [];
      preOrder.pendingPreorderId = localPreOrder?.pendingPreorderId ?? "";
      const merged = restoreCachedCostValues(preOrder, localPreOrder);

      return { customerPreOrder: merged, localPreOrder };
    },
    [call, applyCostValuesFromBasket, restoreCachedCostValues],
  );

  const handlePreorderComplete = useCallback(
    async (completeBookingId: string): Promise<boolean> => {
      try {
        const manageData = await call("/getManageCustomerPreorderDetails", { booking_id: completeBookingId });
        if (manageData?.preorderSettings?.complete) {
          setIsPreorderComplete(true);
          setIsLoadingPreorderData(false);
          return true;
        }
      } catch (error) {
        if (isAbortedRequest(error)) return false;
        console.error("Failed to check preorder completion status:", error);
      }
      return false;
    },
    [call],
  );

  const markPreorderUnavailable = useCallback((error: unknown) => {
    setPreorderUnavailableMessage(error instanceof rpc.RpcError ? (error.bodyMessage ?? null) : null);
    setIsPreorderUnavailable(true);
    setIsLoadingPreorderData(false);
  }, []);

  const handleFallbackLoad = useCallback(
    (fallbackBookingId: string) => {
      call("/getCustomerPreOrder", { booking_id: fallbackBookingId })
        .then((preOrder) => {
          setCustomerPreOrder(preOrder);
          setIsLoadingPreorderData(false);
        })
        .catch((err) => {
          if (isAbortedRequest(err)) return;
          console.error("Final fallback failed:", err);
          markPreorderUnavailable(err);
        });
    },
    [call, markPreorderUnavailable],
  );

  // Takes the basket from the server rather than local state — used when a
  // basket change may have been applied server-side but its response was lost
  // (an interrupted stage change or a timed-out mutation).
  const reloadServerBasket = useCallback(async () => {
    const baskets = await fetchAllBookingTypeBaskets(bookingId);
    setCustomerPreOrder((prev) => {
      const serverBasket = prev ? selectServerBasket(baskets, prev.booking?.type) : undefined;
      if (!prev || !serverBasket) return prev;
      return {
        ...prev,
        items: serverBasket.items,
        forNames: serverBasket.forNames,
        serviceChargePercentage: serverBasket.serviceChargePercentage ?? prev.serviceChargePercentage,
        serviceChargeAmount: serverBasket.serviceChargeAmount ?? prev.serviceChargeAmount,
        totalCost: serverBasket.totalCost ?? prev.totalCost,
        totalCostWithService: serverBasket.totalCostWithService ?? prev.totalCostWithService,
        requiredPaymentAmount: serverBasket.requiredPaymentAmount ?? prev.requiredPaymentAmount,
        tax_inclusive: serverBasket.tax_inclusive ?? prev.tax_inclusive,
        tax_label: serverBasket.tax_label ?? prev.tax_label,
        tax_breakdown: serverBasket.tax_breakdown ?? prev.tax_breakdown,
        total_net: serverBasket.total_net ?? prev.total_net,
        total_gross: serverBasket.total_gross ?? prev.total_gross,
      };
    });
  }, [bookingId, fetchAllBookingTypeBaskets]);

  useEffect(() => {
    // When initialCustomerPreOrder is provided, use it directly and skip API calls.
    // Only apply once to prevent resetting user selections on parent re-renders
    // (pendingBooking is constructed inline in BookingForm, causing reference instability).
    if (initialCustomerPreOrder && !hasAppliedInitialPreOrder.current) {
      hasAppliedInitialPreOrder.current = true;
      setCustomerPreOrder(initialCustomerPreOrder);
      setIsLoadingPreorderData(false);
      if (originalServiceChargePercentage === null && initialCustomerPreOrder.serviceChargePercentage !== undefined) {
        setOriginalServiceChargePercentage(initialCustomerPreOrder.serviceChargePercentage);
      }
      // A basket change was abandoned when the guest last left this step, but the
      // server may have applied it, so take the basket from the server instead.
      if (consumeInterruption?.()) {
        void reloadServerBasket();
      }
      return;
    }

    if (hasAppliedInitialPreOrder.current) return;

    const loadData = async () => {
      try {
        const result = await mergeLocalAndRemotePreorder(bookingId, editingOrderId);
        if (!result) return;

        const { customerPreOrder: loadedPreOrder, localPreOrder } = result;

        if (onLocalPreOrderLoaded) {
          onLocalPreOrderLoaded(editingOrderId ? loadedPreOrder : localPreOrder);
        }
        setCustomerPreOrder(loadedPreOrder);

        // Also fetch all booking type baskets for linked bookings
        await fetchAllBookingTypeBaskets(bookingId);

        setIsLoadingPreorderData(false);

        if (
          loadedPreOrder &&
          originalServiceChargePercentage === null &&
          loadedPreOrder.serviceChargePercentage !== undefined
        ) {
          setOriginalServiceChargePercentage(loadedPreOrder.serviceChargePercentage);
        }
      } catch (e) {
        if (isAbortedRequest(e)) return;
        const isComplete = await handlePreorderComplete(bookingId);
        if (isComplete) return;

        // A 403 means Collins has closed or refused pre-orders for this
        // booking; retrying is guaranteed to fail again.
        if (e instanceof rpc.RpcError && e.status === 403) {
          markPreorderUnavailable(e);
          return;
        }

        console.error("Error loading pre-order data:", e);
        handleFallbackLoad(bookingId);
      }
    };

    loadData();
  }, [
    bookingId,
    editingOrderId,
    originalServiceChargePercentage,
    mergeLocalAndRemotePreorder,
    onLocalPreOrderLoaded,
    handlePreorderComplete,
    handleFallbackLoad,
    markPreorderUnavailable,
    fetchAllBookingTypeBaskets,
    reloadServerBasket,
    initialCustomerPreOrder,
    consumeInterruption,
  ]);

  const updateCustomerPreOrder = useCallback(
    async (request: CustomerPreOrderItemUpdateRequest): Promise<boolean> => {
      if (!customerPreOrder) return false;

      // Name-only updates carry no itemId and lock nothing.
      const pendingKey =
        request.orderingFor !== undefined && request.itemId !== ""
          ? preorderItemKey(request.itemId, request.menuId, request.orderingFor)
          : undefined;
      if (pendingKey) {
        // Several rows can share one key and overlap requests (e.g. the basket
        // auto-assign loop), so track a count rather than membership — the key
        // is only released once the last in-flight request settles.
        setPendingItemCounts((prev) => new Map(prev).set(pendingKey, (prev.get(pendingKey) ?? 0) + 1));
      }

      const effectiveRequest = initialCustomerPreOrder ? { ...request, pending: true } : request;

      // Resolved against the pre-update basket, but only reported once Collins
      // has accepted the change.
      const reportCartChange = describeItemUpdate(customerPreOrder, request);

      try {
        const updates = await call("/updateCustomerPreOrderItems", effectiveRequest);
        if (!updates) return false;

        reportCartChange?.();

        // Merged into the newest state rather than the pre-order captured when
        // the request was sent: state written while it was in flight, such as
        // dietary requirements renamed after a guest's name was added, must not
        // be reverted by the response.
        setCustomerPreOrder((prev) => ({ ...(prev ?? customerPreOrder), ...updates }));

        setExternalValidationErrors((prev) => (prev[request.menuId] ? { ...prev, [request.menuId]: "" } : prev));

        // Refresh multi-booking type data after update to ensure all booking types are visible
        if (request.booking_type) {
          await fetchAllBookingTypeBaskets(request.booking_id || bookingId);
        }

        return true;
      } catch (error) {
        if (isAbortedRequest(error)) return false;
        // The mutation may still have been applied server-side; reload before releasing the caller's guard.
        if (isTimedOutRequest(error)) await reloadServerBasket();
        console.error("Error updating order:", error);

        const serverMessage = error instanceof rpc.RpcError ? error.bodyMessage : undefined;
        const message =
          serverMessage ??
          t("preorder_update_failed", "Sorry, we could not update your order. Please try again or contact the venue.");
        setExternalValidationErrors((prev) => ({ ...prev, [request.menuId]: message }));

        return false;
      } finally {
        if (pendingKey) {
          setPendingItemCounts((prev) => {
            const count = prev.get(pendingKey) ?? 0;
            if (count === 0) return prev;
            const next = new Map(prev);
            if (count === 1) {
              next.delete(pendingKey);
            } else {
              next.set(pendingKey, count - 1);
            }
            return next;
          });
        }
      }
    },
    [call, customerPreOrder, initialCustomerPreOrder, fetchAllBookingTypeBaskets, reloadServerBasket, bookingId, t],
  );

  const isItemPending = useCallback(
    (itemId: string, menuId: string, personIndex: number): boolean =>
      (pendingItemCounts.get(preorderItemKey(itemId, menuId, personIndex)) ?? 0) > 0,
    [pendingItemCounts],
  );

  const refreshCustomerPreOrder = useCallback(
    async (preserveExistingItems = true) => {
      if (!customerPreOrder) return;

      try {
        const updatedPreOrder = await call("/getCustomerPreOrder", {
          booking_id: customerPreOrder.booking._id,
          ...(customerPreOrder.pending ? { pending: true } : {}),
        });
        if (updatedPreOrder) {
          if (preserveExistingItems) {
            updatedPreOrder.items =
              (updatedPreOrder.items?.length ?? 0) > 0 ? updatedPreOrder.items : customerPreOrder.items;
            // GET /getCustomerPreOrder never carries this ID; only getPreorderTotalCost does, so replacing state drops it.
            if (!updatedPreOrder.pendingPreorderId) {
              updatedPreOrder.pendingPreorderId = customerPreOrder.pendingPreorderId;
            }
          }
          updatedPreOrder.dietaryRequirements =
            updatedPreOrder.dietaryRequirements ?? customerPreOrder.dietaryRequirements;

          setCustomerPreOrder(updatedPreOrder);
        }
      } catch (error) {
        if (isAbortedRequest(error)) return;
        console.error("Error refreshing customer preorder:", error);
      }
    },
    [call, customerPreOrder],
  );

  const handlePaymentSuccess = useCallback(
    (email?: string, amount?: number) => {
      if (email) setConfirmedEmail(email);
      if (amount !== undefined) setConfirmedAmount(amount);

      if (customerPreOrder?.items) {
        const itemsWithNames = customerPreOrder.items.map((item) => {
          const forNameIndex = item.for_name_index ?? 0;
          const forName = forNames?.[forNameIndex];
          return {
            ...item,
            for_name: forName,
          };
        });
        setPreservedPreOrderItems({
          items: itemsWithNames,
          totalCost: customerPreOrder.totalCost ?? 0,
          shouldHidePrices,
          hidePricesByMenu,
          serviceChargeAmount: customerPreOrder.serviceChargeAmount,
          serviceChargePercentage: customerPreOrder.serviceChargePercentage,
          totalCostWithService: customerPreOrder.totalCostWithService,
          taxInclusive: customerPreOrder.tax_inclusive,
          taxBreakdown: customerPreOrder.tax_breakdown,
          totalNet: customerPreOrder.total_net,
          totalGross: customerPreOrder.total_gross,
          taxLabel: customerPreOrder.tax_label,
        });
      }
      setCustomerPreOrder(undefined);
      setShowSuccess(true);
    },
    [customerPreOrder, forNames, shouldHidePrices, hidePricesByMenu],
  );

  const savePreorders = useCallback(
    async (details: { name: string; email: string; customerNotes?: string }): Promise<void> => {
      try {
        const updates = await call("/savePreorders", {
          booking_id: bookingId,
          name: details.name,
          email: details.email,
          customer_notes: details.customerNotes,
          dietary_requirements: customerPreOrder?.dietaryRequirements,
          order_id: editingOrderId,
        });

        if (updates) {
          const totalWithService = (customerPreOrder?.totalCost ?? 0) + (customerPreOrder?.serviceChargeAmount ?? 0);
          handlePaymentSuccess(details.email, totalWithService);

          try {
            await call("/clearPreorder", { booking_id: bookingId });
          } catch (error) {
            if (!isAbortedRequest(error)) {
              console.error("Failed to clear preorder after saving:", error);
            }
          }
        }
      } catch (error) {
        if (!isAbortedRequest(error)) {
          console.error("Error saving pre-orders:", error);
        }
        throw error;
      }
    },
    [
      call,
      bookingId,
      editingOrderId,
      handlePaymentSuccess,
      customerPreOrder?.totalCost,
      customerPreOrder?.serviceChargeAmount,
      customerPreOrder?.dietaryRequirements,
    ],
  );

  const handleValidationChange = useCallback((isValid: boolean) => {
    setIsOrderValid(isValid);
  }, []);

  const handleMaxItemsError = useCallback((menuId: string, errorMessage: string) => {
    setExternalValidationErrors((prev) => ({ ...prev, [menuId]: errorMessage }));
  }, []);

  // Clear external validation errors when items are removed and count goes back under the limit
  useEffect(() => {
    const currentItems = customerPreOrder?.items ?? [];

    setExternalValidationErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      let hasChanges = false;

      for (const menuId of Object.keys(newErrors)) {
        if (!newErrors[menuId]) continue;

        const limits = menuLimits[menuId];
        if (!limits?.max) continue;

        const currentCount = getActualItemCountForMenu(currentItems, menuId);

        if (currentCount < limits.max) {
          newErrors[menuId] = "";
          hasChanges = true;
        }
      }

      return hasChanges ? newErrors : prevErrors;
    });
  }, [customerPreOrder?.items, menuLimits]);

  // Automatic minimum spend validation, used only when there is no customerPreOrder to render
  // a <Menu> (e.g. a deposit-only /book flow with no basket yet). Once a customerPreOrder exists,
  // <Menu> owns validation via handleValidationChange (covering both min/max item limits and
  // minimum spend using the real menu data) — this effect must not override that result.
  useEffect(() => {
    if (customerPreOrder) return;

    if (!bookingData || !preorderMenus.length) {
      setIsOrderValid(true); // No menus to validate
      return;
    }

    // If deposit is configured and basket is empty, check if there are any validation requirements
    const hasMinimumSpendRequirements = preorderMenus.some((menu) => (menu.minimum_preorder_amount ?? 0) > 0);

    // Check if any menu has item limit requirements (only min items are blocking, not max)
    const hasItemLimitRequirements = preorderMenus.some((menu) => {
      return menu.limit_preorders?.min && menu.limit_preorders.min > 0;
    });

    // If there are any validation requirements, validate minimum spend
    // Note: Item limits are validated during item addition via validateItemAddition()
    // and through externalValidationErrors, not here
    if (hasMinimumSpendRequirements || hasItemLimitRequirements) {
      const guestCount = bookingData.guestCount;
      const isValid = validateMinimumSpend(preorderMenus, [], guestCount);
      setIsOrderValid(isValid);
    } else {
      // No validation requirements, allow proceeding even with empty basket
      setIsOrderValid(true);
    }
  }, [bookingData, preorderMenus, customerPreOrder]);

  const handleServiceChargeChange = useCallback(
    async (percentage: number): Promise<boolean> => {
      if (!customerPreOrder) return false;

      // Linked bookings: skip service charge update entirely
      const isLinkedBooking =
        Array.isArray(customerPreOrder.booking?.additional_types) &&
        customerPreOrder.booking.additional_types.length > 0;
      if (isLinkedBooking) {
        return true;
      }

      try {
        const updatedPreOrder = await call("/updatePreOrderServiceCharge", {
          booking_id: bookingId,
          pendingPreorderId: customerPreOrder.pendingPreorderId ?? null,
          items: customerPreOrder.items ?? [],
          booking: customerPreOrder.booking,
          serviceChargePercentage: percentage,
          ...(initialCustomerPreOrder ? { pending: true } : {}),
        });
        // Use a functional updater so that if an item update completes between
        // the dispatch and this callback, the freshly-merged item state isn't
        // overwritten by the stale `customerPreOrder` captured in the closure.
        setCustomerPreOrder((prev) => ({ ...(prev ?? customerPreOrder), ...updatedPreOrder }));
        return true;
      } catch (error) {
        if (isAbortedRequest(error)) return false;
        // The service charge may still have been applied server-side; reload before releasing the caller's guard.
        if (isTimedOutRequest(error)) await reloadServerBasket();
        console.error("Failed to update service charge:", error);
        return false;
      }
    },
    [call, bookingId, customerPreOrder, initialCustomerPreOrder, reloadServerBasket],
  );

  return {
    customerPreOrder,
    customerPreOrders,
    setCustomerPreOrder,
    setCustomerPreOrders,
    preservedPreOrderItems,
    isLoadingPreorderData,
    isPreorderComplete,
    isPreorderUnavailable,
    preorderUnavailableMessage,
    preorderLimitMessage,
    menuLimits,
    setMenuLimits,
    externalValidationErrors,
    originalServiceChargePercentage,
    confirmedEmail,
    confirmedAmount,
    showSuccess,
    updateCustomerPreOrder,
    isItemPending,
    refreshCustomerPreOrder,
    reloadServerBasket,
    handleServiceChargeChange,
    handleValidationChange,
    handleMaxItemsError,
    handlePaymentSuccess,
    savePreorders,
    isOrderValid,
    hidePricesByMenu,
    shouldHidePrices,
    paidByGroupMember,
  };
}

export type { PreservedPreOrderItems };
